import { classNames, fuzzySearchMutipleWords } from "@/lib/util";
import {
  OrderCarouselQuery,
  useUpdateManyRecipesMutation,
} from "@/queries/autogenerate/graphql";
import {
  ArchiveBoxIcon,
  ChevronDownIcon,
  LockClosedIcon,
  LockOpenIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { FolderIcon, FolderOpenIcon } from "@heroicons/react/24/solid";
import { format } from "date-fns";
import Link from "next/link";
import React, {
  Dispatch,
  SetStateAction,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import DialogComponent from "../Dialog";
import { CurrentUser } from "../Page";
import Tooltip from "../styles/Tooltip";
import RecipesListItem from "./RecipesListItem";
import { getPathToFolder } from "./util";

interface Props {
  user: CurrentUser;
  fragmentID?: string;
  recipes: OrderCarouselQuery["recipes"];
  sortedRecipes: OrderCarouselQuery["recipes"];
  selectedRecipes: string[];
  setSelectedRecipes: Dispatch<SetStateAction<string[]>>;
  limit: number;
  setLimit: Dispatch<SetStateAction<number>>;
  orderBy: {
    label: string;
    value: string;
  };
  setOrderBy: (newValue: { label: string; value: string }) => void;
  folder:
    | NonNullable<CurrentUser["school"]>["recipeCollections"][0]
    | null
    | undefined;
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  openEditRecipeFolder: (id: string) => void;
  openDeleteRecipeFolder: (id: string) => void;
  search: string;
}

const RecipesList: React.FC<Props> = ({
  user,
  fragmentID,
  recipes,
  sortedRecipes,
  selectedRecipes,
  setSelectedRecipes,
  limit,
  setLimit,
  orderBy,
  setOrderBy,
  folder,
  sortedFolders,
  openEditRecipeFolder,
  openDeleteRecipeFolder,
  search,
}) => {
  const [{ fetching }, updateManyRecipes] = useUpdateManyRecipesMutation();

  const checkbox = useRef<HTMLInputElement>(null);
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);

  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedRecipes.length > 0 && selectedRecipes.length < recipes.length;
    setChecked(selectedRecipes.length === recipes.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) {
      checkbox.current.indeterminate = isIndeterminate;
    }
  }, [selectedRecipes]);

  function toggleAll() {
    setSelectedRecipes(
      checked || indeterminate ? [] : recipes.map((r) => r.id)
    );
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  const displayFolders = search
    ? fuzzySearchMutipleWords(user.school?.recipeCollections || [], search, {
        keys: [
          "name",
          (item) => format(new Date(item.createdAt), "p EEEE LLLL do, yyyy"),
        ],
      })
    : sortedFolders.filter((rc) =>
        folder ? rc.belongsToId === folder.id : !rc.belongsToId
      );

  return (
    <div className="mt-8 flow-root">
      <div className="-mx-4 -my-2 max-w-full overflow-x-auto min-lg:-mx-8 min-sm:-mx-6">
        <div className="inline-block min-w-full py-2 align-middle min-lg:px-8 min-sm:px-6">
          <div className="relative">
            {selectedRecipes.length > 0 && (
              <div className="absolute left-14 top-0 flex h-12 items-center space-x-1.5 bg-gray-100 min-sm:left-12">
                {/* <button
                  type="button"
                  className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                >
                  Bulk edit
                </button> */}
                <Tooltip content="Lock The Selected Recipes">
                  <button
                    onClick={async () => {
                      toast
                        .promise(
                          updateManyRecipes({
                            where: { id: { in: selectedRecipes } },
                            data: {
                              locked: { set: true },
                            },
                          }),
                          {
                            loading: "Locking Recipes...",
                            success: "Recipes Locked!",
                            error: "Error Locking Recipes",
                          }
                        )
                        .catch((error) => console.error(error));
                    }}
                  >
                    {fetching ? (
                      <div className="spinner h-3 w-3" />
                    ) : (
                      <LockClosedIcon className="icon" />
                    )}
                  </button>
                </Tooltip>

                <Tooltip content="Unlock The Selected Recipes">
                  <button
                    onClick={async () => {
                      toast
                        .promise(
                          updateManyRecipes({
                            where: { id: { in: selectedRecipes } },
                            data: {
                              locked: { set: false },
                            },
                          }),
                          {
                            loading: "Unlocking Recipes...",
                            success: "Recipes Unlocked!",
                            error: "Error Unlocking Recipes",
                          }
                        )
                        .catch((error) => console.error(error));
                    }}
                  >
                    {fetching ? (
                      <div className="spinner h-3 w-3" />
                    ) : (
                      <LockOpenIcon className="icon" />
                    )}
                  </button>
                </Tooltip>

                <Tooltip content="Archive The Selected Recipes">
                  <button
                    onClick={async () => {
                      setArchiveDialogOpen(true);
                    }}
                  >
                    {fetching ? (
                      <div className="spinner h-3 w-3" />
                    ) : (
                      <ArchiveBoxIcon className="icon" />
                    )}
                  </button>
                </Tooltip>
                <DialogComponent
                  isOpen={archiveDialogOpen}
                  onClose={() => setArchiveDialogOpen(false)}
                  aria-label="Archive Many Recipes"
                >
                  <h2>Archive {selectedRecipes.length} Recipes</h2>
                  <p>Are you sure you want to archive the selected recipes?</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => setArchiveDialogOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={async () => {
                      toast
                        .promise(
                          updateManyRecipes({
                            where: { id: { in: selectedRecipes } },
                            data: {
                              archivedAt: { set: new Date() },
                            },
                          }),
                          {
                            loading: "Archiving Recipes...",
                            success: "Recipes Archived!",
                            error: "Error Archiving Recipes",
                          }
                        )
                        .catch((error) => console.error(error));
                    }}
                    disabled={fetching}
                  >
                    {fetching ? <div className="spinner"></div> : null}
                    Archive Recipes
                  </button>
                </DialogComponent>

                <Tooltip content="Delete The Selected Recipes">
                  <button
                    onClick={async () => {
                      setDeleteDialogOpen(true);
                    }}
                  >
                    {fetching ? (
                      <div className="spinner h-3 w-3" />
                    ) : (
                      <TrashIcon className="icon icon-orange" />
                    )}
                  </button>
                </Tooltip>
                <DialogComponent
                  isOpen={deleteDialogOpen}
                  onClose={() => setDeleteDialogOpen(false)}
                  aria-label="Delete Many Recipes"
                >
                  <h2>Delete {selectedRecipes.length} Recipes</h2>
                  <p>Are you sure you want to delete the selected recipes?</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => setDeleteDialogOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={async () => {
                      toast
                        .promise(
                          updateManyRecipes({
                            where: { id: { in: selectedRecipes } },
                            data: {
                              deletedAt: { set: new Date() },
                            },
                          }),
                          {
                            loading: "Deleting Recipes...",
                            success: "Recipes Deleted!",
                            error: "Error Deleting Recipes",
                          }
                        )
                        .catch((error) => console.error(error));
                    }}
                    disabled={fetching}
                  >
                    {fetching ? <div className="spinner"></div> : null}
                    Delete Recipes
                  </button>
                </DialogComponent>

                {/* <button
                  type="button"
                  className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                >
                  Delete all
                </button> */}
              </div>
            )}
            <table className="min-w-full table-fixed divide-y divide-gray-300">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="relative px-7 min-sm:w-12 min-sm:px-6"
                  >
                    <input
                      type="checkbox"
                      className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                      ref={checkbox}
                      checked={checked}
                      onChange={toggleAll}
                    />
                  </th>
                  <th
                    scope="col"
                    className="xmin-w-[12rem] xmax-w-[8rem] cursor-pointer py-3.5 pr-3 text-left text-sm font-semibold text-gray-900"
                    onClick={() => {
                      if (orderBy.value == "name_ASC") {
                        setOrderBy({
                          label: "Alphabetical (descending)",
                          value: "name_DESC",
                        });
                      } else {
                        setOrderBy({
                          label: "Alphabetical",
                          value: "name_ASC",
                        });
                      }
                    }}
                  >
                    Name
                    {["name_ASC", "name_DESC"].includes(orderBy.value) ? (
                      <ChevronDownIcon
                        className={classNames(
                          "ml-2 w-5 text-gray-400 transition",
                          orderBy.value === "name_DESC"
                            ? "rotate-180 transform"
                            : ""
                        )}
                      />
                    ) : null}
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    onClick={() => {
                      if (orderBy.value == "class_DESC") {
                        setOrderBy({
                          label: "Class",
                          value: "class_ASC",
                        });
                      } else {
                        setOrderBy({
                          label: "Class (descending)",
                          value: "class_DESC",
                        });
                      }
                    }}
                  >
                    Class(es)
                    {["class_ASC", "class_DESC"].includes(orderBy.value) ? (
                      <ChevronDownIcon
                        className={classNames(
                          "ml-2 w-5 text-gray-400 transition",
                          orderBy.value === "class_DESC"
                            ? "rotate-180 transform"
                            : ""
                        )}
                      />
                    ) : null}
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    onClick={() => {
                      if (orderBy.value == "tag_DESC") {
                        setOrderBy({
                          label: "Tags",
                          value: "tag_ASC",
                        });
                      } else {
                        setOrderBy({
                          label: "Tags (descending)",
                          value: "tag_DESC",
                        });
                      }
                    }}
                  >
                    Tags
                    {["tag_ASC", "tag_DESC"].includes(orderBy.value) ? (
                      <ChevronDownIcon
                        className={classNames(
                          "ml-2 w-5 text-gray-400 transition",
                          orderBy.value === "tag_DESC"
                            ? "rotate-180 transform"
                            : ""
                        )}
                      />
                    ) : null}
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    onClick={() => {
                      if (orderBy.value == "createdAt_DESC") {
                        setOrderBy({
                          label: "Oldest",
                          value: "createdAt_ASC",
                        });
                      } else {
                        setOrderBy({
                          label: "Newest",
                          value: "createdAt_DESC",
                        });
                      }
                    }}
                  >
                    Created
                    {["createdAt_ASC", "createdAt_DESC"].includes(
                      orderBy.value
                    ) ? (
                      <ChevronDownIcon
                        className={classNames(
                          "ml-2 w-5 text-gray-400 transition",
                          orderBy.value === "createdAt_DESC"
                            ? "rotate-180 transform"
                            : ""
                        )}
                      />
                    ) : null}
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    onClick={() => {
                      if (orderBy.value == "updatedAt_DESC") {
                        setOrderBy({
                          label: "First Updated",
                          value: "updatedAt_ASC",
                        });
                      } else {
                        setOrderBy({
                          label: "Last Updated",
                          value: "updatedAt_DESC",
                        });
                      }
                    }}
                  >
                    Updated
                    {["updatedAt_ASC", "updatedAt_DESC"].includes(
                      orderBy.value
                    ) ? (
                      <ChevronDownIcon
                        className={classNames(
                          "ml-2 w-5 text-gray-400 transition",
                          orderBy.value === "updatedAt_DESC"
                            ? "rotate-180 transform"
                            : ""
                        )}
                      />
                    ) : null}
                  </th>
                  {/* <th
                    scope="col"
                    className="relative py-3.5 pl-3 pr-4 min-sm:pr-3"
                  >
                    <span className="sr-only">Actions</span>
                  </th> */}
                  <th
                    scope="col"
                    className="relative py-3.5 pl-3 pr-4 min-sm:pr-3"
                  >
                    <span className="sr-only">More</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {displayFolders.map((folder) => (
                  <tr key={folder.id} className="bg-blue-50">
                    {/* <td className="relative px-7 min-sm:w-12 min-sm:px-6">
                        {selectedRecipes.includes(recipe.id) && (
                          <div className="absolute inset-y-0 left-0 w-0.5 bg-blue-600" />
                        )}
                        <input
                          type="checkbox"
                          className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                          value={recipe.id}
                          checked={selectedRecipes.includes(recipe.id)}
                          onChange={(e) =>
                            setSelectedRecipes(
                              e.target.checked
                                ? [...selectedRecipes, recipe.id]
                                : selectedRecipes.filter(
                                    (id) => id !== recipe.id
                                  )
                            )
                          }
                        />
                      </td> */}
                    <td
                      className="group max-w-[12rem] truncate whitespace-nowrap py-4 pr-3 text-lg font-medium"
                      colSpan={4}
                    >
                      <Link
                        prefetch={false}
                        href={`/recipes?folder=${folder.id}`}
                        className="flex items-center gap-2"
                      >
                        <FolderIcon className="mx-4 w-18 text-blue-400 group-hover:hidden" />
                        <FolderOpenIcon className="mx-4 hidden w-18 text-blue-400 group-hover:inline-block" />
                        <div className="flex flex-col gap-1 truncate">
                          <span title={folder.name} className="truncate">
                            {folder.name}
                          </span>
                          <div className="flex items-center gap-2 font-normal text-gray-400">
                            <span className="font-normal text-gray-400">
                              {
                                recipes.filter(
                                  (r) => r.recipeCollection?.id === folder.id
                                ).length
                              }{" "}
                              recipes
                            </span>
                            ·
                            <span className="font-normal text-gray-400">
                              {
                                sortedFolders.filter(
                                  (f) => f.belongsToId === folder.id
                                ).length
                              }{" "}
                              folders
                            </span>
                          </div>
                          {search ? (
                            <div className="flex items-center gap-2 rounded border border-blue-400 border-opacity-50 bg-white p-1 text-sm">
                              <FolderIcon className="w-5 text-blue-400" />{" "}
                              {getPathToFolder(
                                sortedFolders.find((f) => f.id == folder.id),
                                sortedFolders
                              )
                                .map(
                                  (id) =>
                                    sortedFolders.find((c) => c.id === id)
                                      ?.name || "unknown folder"
                                )
                                .join(" / ")}
                            </div>
                          ) : null}
                        </div>
                      </Link>
                    </td>
                    {/* <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
          {recipe.servings}
        </td> */}
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {format(new Date(folder.createdAt), "dd/MM/yy")}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {format(new Date(folder.updatedAt), "dd/MM/yy")}
                    </td>
                    <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium min-sm:pr-3">
                      {/* Actions etc. */}
                      <div className="flex items-center justify-end gap-2">
                        <Tooltip content="Delete Folder">
                          <button
                            onClick={() => openDeleteRecipeFolder(folder.id)}
                          >
                            <TrashIcon className="w-5 text-orange-600 hover:text-orange-700" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Edit Folder">
                          <button
                            onClick={() => openEditRecipeFolder(folder.id)}
                          >
                            <PencilIcon className="w-5 text-cyan-700 hover:text-cyan-900" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
                {sortedRecipes.map((recipe) => (
                  <RecipesListItem
                    key={recipe.id}
                    recipe={recipe}
                    selectedRecipes={selectedRecipes}
                    setSelectedRecipes={setSelectedRecipes}
                    folder={folder}
                    sortedFolders={sortedFolders}
                    search={search}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipesList;
